import { ApiResponse, PaginatedResponse } from '@/types';

/**
 * Servicio base para comunicación con la API
 * Proporciona métodos comunes para todas las operaciones CRUD
 */
export class BaseService {
  protected baseURL: string;
  protected endpoint: string;

  constructor(endpoint: string) {
    this.baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';
    this.endpoint = endpoint;
  }

  /**
   * Obtiene los headers por defecto para las peticiones
   */
  protected getHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  /**
   * Maneja la respuesta de la API con mejor control de errores JSON
   */
  protected async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorData;
      try {
        const text = await response.text();
        errorData = text ? JSON.parse(text) : {};
      } catch (jsonError) {
        errorData = { message: `Error del servidor (${response.status}): ${response.statusText}` };
      }
      throw new Error(errorData.message || `HTTP error! status: ${response.status} - ${response.statusText}`);
    }
    
    try {
      const text = await response.text();
      if (!text) {
        return { success: true, message: 'Operación completada exitosamente' } as T;
      }
      const parsed = JSON.parse(text);
      // Asegurar que siempre tengamos un mensaje
      if (parsed && typeof parsed === 'object' && !parsed.message && !parsed.success) {
        parsed.message = 'Operación completada';
      }
      return parsed;
    } catch (jsonError) {
      throw new Error('Respuesta del servidor no válida');
    }
  }

  /**
   * Realiza una petición GET personalizada
   * @param path Ruta adicional al endpoint base
   * @param params Parámetros de consulta (query params)
   * @returns Promise con la respuesta de la API
   */
  async get<T>(path: string = '', params?: Record<string, any>): Promise<ApiResponse<T>> {
    let url = path.startsWith('/') ? `${this.baseURL}${this.endpoint}${path}` : `${this.baseURL}${this.endpoint}/${path}`;
    
    // Añadir parámetros de consulta si existen
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += url.includes('?') ? `&${queryString}` : `?${queryString}`;
      }
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders()
    });
    return this.handleResponse<ApiResponse<T>>(response);
  }

  /**
   * Realiza una petición POST personalizada
   * @param path Ruta adicional al endpoint base
   * @param data Datos a enviar
   * @returns Promise con la respuesta de la API
   */
  async post<T>(path: string = '', data?: any): Promise<ApiResponse<T>> {
    const url = path ? `${this.baseURL}${this.endpoint}/${path}` : `${this.baseURL}${this.endpoint}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined
    });
    return this.handleResponse<ApiResponse<T>>(response);
  }

  /**
   * Realiza una petición PUT personalizada
   * @param path Ruta adicional al endpoint base
   * @param data Datos a enviar
   * @returns Promise con la respuesta de la API
   */
  async put<T>(path: string = '', data?: any): Promise<ApiResponse<T>> {
    const url = path ? `${this.baseURL}${this.endpoint}/${path}` : `${this.baseURL}${this.endpoint}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined
    });
    return this.handleResponse<ApiResponse<T>>(response);
  }

  protected async putWithUploadProgress<T>(
    path: string = '',
    data: any,
    onUploadProgress?: (percentage: number) => void
  ): Promise<ApiResponse<T>> {
    if (!onUploadProgress) return this.put<T>(path, data);

    const url = path ? `${this.baseURL}${this.endpoint}/${path}` : `${this.baseURL}${this.endpoint}`;
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);

      const headers = this.getHeaders() as Record<string, string>;
      Object.entries(headers).forEach(([key, value]) => {
        if (value) xhr.setRequestHeader(key, value);
      });

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        const percentage = Math.min(95, Math.round((event.loaded / event.total) * 100));
        onUploadProgress(percentage);
      };

      xhr.onload = () => {
        onUploadProgress(100);
        const response = new Response(xhr.responseText, {
          status: xhr.status,
          statusText: xhr.statusText
        });
        this.handleResponse<ApiResponse<T>>(response).then(resolve).catch(reject);
      };

      xhr.onerror = () => reject(new Error('Error de red durante la subida de datos'));
      xhr.send(data ? JSON.stringify(data) : undefined);
    });
  }

  /**
   * Obtiene todos los elementos
   */
  async getAll<T>(): Promise<ApiResponse<T[]>> {
    const response = await fetch(`${this.baseURL}${this.endpoint}`, {
      method: 'GET',
      headers: this.getHeaders()
    });
    return this.handleResponse<ApiResponse<T[]>>(response);
  }

  /**
   * Obtiene elementos paginados
   */
  async getPaginated<T>(page: number = 0, size: number = 10): Promise<PaginatedResponse<T>> {
    const response = await fetch(`${this.baseURL}${this.endpoint}?page=${page}&size=${size}`, {
      method: 'GET',
      headers: this.getHeaders()
    });
    return this.handleResponse<PaginatedResponse<T>>(response);
  }

  /**
   * Obtiene un elemento por ID
   */
  async getById<T>(id: number): Promise<ApiResponse<T>> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/${id}`, {
      method: 'GET',
      headers: this.getHeaders()
    });
    return this.handleResponse<ApiResponse<T>>(response);
  }

  /**
   * Crea un nuevo elemento
   */
  async create<T>(data: Partial<T>): Promise<ApiResponse<T>> {
    const response = await fetch(`${this.baseURL}${this.endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });
    return this.handleResponse<ApiResponse<T>>(response);
  }

  /**
   * Actualiza un elemento existente
   */
  async update<T>(id: number, data: Partial<T>): Promise<ApiResponse<T>> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/${id}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });
    return this.handleResponse<ApiResponse<T>>(response);
  }

  /**
   * Elimina un elemento
   */
  async delete(id: number): Promise<ApiResponse<void>> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });
    return this.handleResponse<ApiResponse<void>>(response);
  }
}
